import { Children, ReactNode } from 'react';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

/**
 * This interface was (mostly) generated with Gemini.
 * I haven't verified that its comments are completely accurate.
 */
interface CSSTransitionGroupProps {
    /**
     * The prefix for the CSS class names applied during transitions.
     */
    transitionName: string;

    /**
     * Whether to enable the "enter" animation for new components.
     * Defaults to `true`.
     */
    transitionEnter?: boolean;

    /**
     * Whether to enable the "leave" animation for removed components.
     * Defaults to `true`.
     */
    transitionLeave?: boolean;

    /**
     * Whether to enable the "appear" animation for components mounted
     * on the initial render. Defaults to `false`.
     */
    transitionAppear?: boolean;

    /**
     * The duration (in milliseconds) for the "enter" transition.
     * This is required if `transitionEnter` is `true`.
     */
    transitionEnterTimeout?: number;

    /**
     * The duration (in milliseconds) for the "leave" transition.
     * This is required if `transitionLeave` is `true`.
     */
    transitionLeaveTimeout?: number;

    /**
     * The duration (in milliseconds) for the "appear" transition.
     * This is required if `transitionAppear` is `true`.
     */
    transitionAppearTimeout?: number;

    /**
     * The child elements to be animated. Each direct child must have a unique `key` prop.
     */
    children?: ReactNode | null;
}

/**
 * For backward compatibility with V1 of react-transition-group
 * @deprecated Directly import from modern react-transition-group instead
 * @param children
 * @param transitionName
 * @param transitionEnter
 * @param transitionAppear
 * @param transitionLeave
 * @param transitionAppearTimeout
 * @param transitionLeaveTimeout
 * @param transitionEnterTimeout
 * @constructor
 */
const CSSTransitionGroup = ({
    children,
    transitionName,
    transitionEnter,
    transitionAppear,
    transitionLeave,
    transitionAppearTimeout,
    transitionLeaveTimeout,
    transitionEnterTimeout,
}: CSSTransitionGroupProps) => {
    const content = children && Children.map(children, child =>
        <CSSTransition
            classNames={transitionName}
            appear={transitionAppear}
            enter={transitionEnter}
            leave={transitionLeave}
            timeout={{
                appear: transitionAppearTimeout,
                enter: transitionEnterTimeout,
                exit: transitionLeaveTimeout,
            }}
        >
            {child}
        </CSSTransition>,
    );

    return (
        <TransitionGroup>
            {content}
        </TransitionGroup>
    );
};

export default CSSTransitionGroup;
